import React, { useId, useState, useCallback } from 'react';
import PropTypes from 'prop-types';
import { Plus, Minus } from 'lucide-react';
import './FAQItem.css';

/**
 * FAQItem — accordion item used by brand FAQ pages.
 *
 * Two visual modes (Figma):
 * - Collapsed:  4029:756  (Plus icon, no body)
 * - Expanded:   4029:758  (Minus icon, body visible, gap small-l above body)
 *
 * Controlled (`expanded` + `onToggle`) or uncontrolled (`defaultExpanded`).
 * Smooth height animation via CSS grid-template-rows trick — no JS height
 * measurement, works with dynamic content.
 */
const FAQItem = ({
  question,
  answer,
  expanded: controlledExpanded,
  defaultExpanded = false,
  onToggle,
  id,
  className = '',
}) => {
  const reactId = useId();
  const panelId = id ? `${id}-panel` : `faq-${reactId}-panel`;

  const isControlled = controlledExpanded !== undefined;
  const [internalExpanded, setInternalExpanded] = useState(defaultExpanded);
  const expanded = isControlled ? controlledExpanded : internalExpanded;

  const handleToggle = useCallback(() => {
    const next = !expanded;
    if (!isControlled) setInternalExpanded(next);
    if (onToggle) onToggle(next);
  }, [expanded, isControlled, onToggle]);

  const rootClass = [
    'ds-faq-item',
    expanded ? 'ds-faq-item--expanded' : '',
    className,
  ].filter(Boolean).join(' ');

  return (
    <div className={rootClass}>
      <button
        type="button"
        className="ds-faq-item__header"
        aria-expanded={expanded}
        aria-controls={panelId}
        onClick={handleToggle}
      >
        <span className="ds-faq-item__question typo-h4">{question}</span>
        <span className="ds-faq-item__icon" aria-hidden="true">
          {expanded ? <Minus size={20} strokeWidth={1.5} /> : <Plus size={20} strokeWidth={1.5} />}
        </span>
      </button>
      <div
        id={panelId}
        role="region"
        className="ds-faq-item__panel"
        aria-hidden={!expanded}
      >
        <div className="ds-faq-item__panel-inner">
          <div className="ds-faq-item__answer typo-body">{answer}</div>
        </div>
      </div>
    </div>
  );
};

FAQItem.propTypes = {
  question: PropTypes.node.isRequired,
  answer: PropTypes.node.isRequired,
  expanded: PropTypes.bool,
  defaultExpanded: PropTypes.bool,
  onToggle: PropTypes.func,
  id: PropTypes.string,
  className: PropTypes.string,
};

export default FAQItem;
